import { Product } from "./types/product";
import { SORTED_SIZES } from "./constants";
import { SelectedVariant } from "./types/variant";

/**
 * @param String with words separated by spaces or underscores
 * @returns String with the first letter of each word capitalized without removing special characters
 *
 * @example
 * toCapitalized("hello world") // "Hello World"
 * toCapitalized("hello_world") // "Hello World"
 * toCapitalized("hello / world") // "Hello / World"
 */
export function toCapitalized(str: string) {
  return str
    .replace(/_/g, " ")
    .replace(
      /\w+/g,
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    );
}

export function formatCurrency(
  amount: number,
  options?: Intl.NumberFormatOptions,
) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    ...options,
  }).format(amount);
}

export function formatPercent(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(amount);
}

export const productNameFrom = (product: Product, nowrap = true): string => {
  const hyphenatedStr = /(?=\S*['-])([a-zA-Z'-]+)/g;
  let name = product.name;

  if (nowrap) {
    name = product.name
      .split(" ")
      .map((word) =>
        word.replace(
          hyphenatedStr,
          `<span class="whitespace-nowrap">${word}</span>`,
        ),
      )
      .join(" ");
  }

  return [product.mill?.name, product.styleCode, name].join(" ");
};

export function sortBySize(a: { size: string }, b: { size: string }) {
  const sizeOrder = (item: { size: string }) => {
    return SORTED_SIZES.indexOf(item.size.toUpperCase());
  };
  return sizeOrder(a) - sizeOrder(b);
}

export function selectedVariantFrom(
  id: string | null,
  product: Product,
): SelectedVariant {
  const defaultSelectedVariant = (() => {
    const colorVariant = product.colors.find(
      (c) => c.color === product.defaultVariant.color,
    );

    if (!colorVariant) throw Error("Default variant color not found");

    return {
      ...colorVariant,
      ...product.defaultVariant,
    };
  })();

  if (!id) return defaultSelectedVariant;

  const variant = product.variants.find((variant) => variant.id === id);

  if (!variant) return defaultSelectedVariant;

  const colorVariant = product.colors.find((c) => c.color === variant.color);

  if (!colorVariant) return defaultSelectedVariant;

  return { ...variant, ...colorVariant };
}
